import React from 'react';
import BRAND from '../brandConfig';
import { useApp } from '../context/AppContext';
import BrandMark from '../components/BrandMark';
import { useI18n } from '../i18n';
import LanguageSwitcher from '../components/LanguageSwitcher';

// Public nav bar shared by the auth screens: brand + + back button.
const AuthNav = ({ onBack }) => {
  const { t } = useI18n();
  return (
    <nav className="nav-public">
      <BrandMark />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <LanguageSwitcher />
        <button className="btn btn-ghost btn-sm" onClick={onBack}>{t('nav.back')}</button>
      </div>
    </nav>
  );
};

// Screen where the user chooses Homeowner vs Technician sign-in.
export function LoginChoice() {
  const { currentScreen, goTo } = useApp();
  const { t } = useI18n();
  return (
    <div className={`screen ${currentScreen === 'screen-login-choice' ? 'active' : ''}`}>
      <AuthNav onBack={() => goTo('screen-home')} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 26, color: 'var(--navy)', marginBottom: 8 }}>{t('auth.welcomeBack')}</div>
        <p style={{ color: 'var(--muted)', fontSize: 14, marginBottom: 32, textAlign: 'center' }}>{t('auth.chooseHow')}</p>
        <div style={{ width: '100%', maxWidth: 360 }}>
          <div className="portal-line" onClick={() => goTo('screen-homeowner-login')}>
            <span className="portal-line-title">{t('auth.homeowner')}</span>
            <span className="portal-line-arrow">{t('home.signInArrow')}</span>
          </div>
          <div className="portal-line" onClick={() => goTo('screen-vendor-login')}>
            <span className="portal-line-title">{t('auth.technician')}</span>
            <span className="portal-line-arrow">{t('home.signInArrow')}</span>
          </div>
          <div className="portal-line" onClick={() => goTo('screen-account-login')}>
            <span className="portal-line-title">{t('auth.commercial')}</span>
            <span className="portal-line-arrow">{t('home.signInArrow')}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// Commercial Accounts sign-in / create-account.
//
// Property managers, HOAs, realtors and investors. Signing up creates the
// company and makes you its owner, so the create-account form asks for the
// company name — it is the one field that can't be filled in later without
// somebody wondering whose account they are looking at.
//
// No SMS consent box here. A management company's texts are about work orders
// on properties they manage, which the account relationship already covers; the
// consent record that matters for the A2P audit is the tenant's, captured on
// the work order itself.
export function AccountLogin() {
  const { currentScreen, goTo, authForm, setAuthForm, authError, handleAuthInputChange, handleAuthSubmit, openForgotPassword } = useApp();
  const { t } = useI18n();
  return (
    <div className={`screen ${currentScreen === 'screen-account-login' ? 'active' : ''}`}>
      <AuthNav onBack={() => goTo('screen-home')} />
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px 20px' }}>
        <div className="auth-card">
          <div className="auth-title">{t('auth.commercialTitle')}</div>
          <div className="auth-sub">{t('auth.commercialSub')}</div>
          <div className="auth-toggle">
            <button className={`auth-toggle-btn ${authForm.mode === 'login' ? 'active' : ''}`} onClick={() => setAuthForm(p => ({ ...p, mode: 'login' }))}>{t('auth.signIn')}</button>
            <button className={`auth-toggle-btn ${authForm.mode === 'register' ? 'active' : ''}`} onClick={() => setAuthForm(p => ({ ...p, mode: 'register' }))}>{t('auth.createAccount')}</button>
          </div>
          {authError && <div className="alert alert-red" style={{ marginBottom: 14 }}>{authError}</div>}
          {authForm.mode === 'register' && (
            <>
              <div className="form-group">
                <label>{t('acct.companyName')} <span className="req">*</span></label>
                <input type="text" name="companyName" value={authForm.companyName || ''} onChange={handleAuthInputChange} placeholder="Cedar Property Management" />
              </div>
              <div className="form-row">
                <div className="form-group"><label>{t('req.fullName')}</label><input type="text" name="name" value={authForm.name} onChange={handleAuthInputChange} placeholder="Dana Reyes" /></div>
                <div className="form-group"><label>{t('req.phone')}</label><input type="tel" name="phone" value={authForm.phone} onChange={handleAuthInputChange} placeholder="(801) 555-0100" /></div>
              </div>
            </>
          )}
          <div className="form-group"><label>{t('req.email')}</label><input type="email" name="email" value={authForm.email} onChange={handleAuthInputChange} placeholder="you@company.com" /></div>
          <div className="form-group"><label>{t('auth.password')}</label><input type="password" name="password" value={authForm.password} onChange={handleAuthInputChange} placeholder="••••••••" /></div>
          <button className="btn btn-primary btn-lg" onClick={() => handleAuthSubmit('manager')}>
            {authForm.mode === 'login' ? t('auth.signIn') : t('auth.createAccount')}
          </button>
          {authForm.mode === 'login' && (
            <span className="form-link" onClick={() => openForgotPassword('screen-account-login')}>{t('auth.forgot')}</span>
          )}
        </div>
      </div>
    </div>
  );
}

// Homeowner sign-in / create-account.
export function HomeownerLogin() {
  const { currentScreen, goTo, authForm, setAuthForm, authError, handleAuthInputChange, handleAuthSubmit, openForgotPassword } = useApp();
  const { t } = useI18n();
  return (
    <div className={`screen ${currentScreen === 'screen-homeowner-login' ? 'active' : ''}`}>
      <AuthNav onBack={() => goTo('screen-home')} />
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px 20px' }}>
        <div className="auth-card">
          <div className="auth-title">{t('auth.hoTitle')}</div>
          <div className="auth-sub">{t('auth.hoSub')}</div>
          <div className="auth-toggle">
            <button className={`auth-toggle-btn ${authForm.mode === 'login' ? 'active' : ''}`} onClick={() => setAuthForm(p => ({ ...p, mode: 'login' }))}>{t('auth.signIn')}</button>
            <button className={`auth-toggle-btn ${authForm.mode === 'register' ? 'active' : ''}`} onClick={() => setAuthForm(p => ({ ...p, mode: 'register' }))}>{t('auth.createAccount')}</button>
          </div>
          {authError && <div className="alert alert-red" style={{ marginBottom: 14 }}>{authError}</div>}
          {authForm.mode === 'register' && (
            <div className="form-row">
              <div className="form-group"><label>{t('req.fullName')}</label><input type="text" name="name" value={authForm.name} onChange={handleAuthInputChange} placeholder="Jane Doe" /></div>
              <div className="form-group"><label>{t('req.phone')}</label><input type="tel" name="phone" value={authForm.phone} onChange={handleAuthInputChange} placeholder="(801) 555-0100" /></div>
            </div>
          )}
          <div className="form-group"><label>{t('req.email')}</label><input type="email" name="email" value={authForm.email} onChange={handleAuthInputChange} placeholder="jane@email.com" /></div>
          <div className="form-group"><label>{t('auth.password')}</label><input type="password" name="password" value={authForm.password} onChange={handleAuthInputChange} placeholder="••••••••" /></div>
          <button className="btn btn-primary btn-lg" style={{ marginBottom: 12 }} onClick={() => handleAuthSubmit('homeowner')}>
            {authForm.mode === 'login' ? t('auth.signIn') : t('auth.createAccount')}
          </button>
          {authForm.mode === 'login' && (
            <span className="form-link" onClick={() => openForgotPassword('screen-homeowner-login')}>{t('auth.forgot')}</span>
          )}
          <span className="form-link" onClick={() => goTo('screen-request')}>{t('auth.requestWithout')}</span>
        </div>
      </div>
    </div>
  );
}

// Technician / vendor sign-in / create-account.
export function VendorLogin() {
  const { currentScreen, goTo, authForm, setAuthForm, authError, handleAuthInputChange, handleAuthSubmit, openForgotPassword } = useApp();
  const { t } = useI18n();
  return (
    <div className={`screen ${currentScreen === 'screen-vendor-login' ? 'active' : ''}`}>
      <AuthNav onBack={() => goTo('screen-home')} />
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px 20px' }}>
        <div className="auth-card">
          <div className="auth-title">{t('auth.techTitle')}</div>
          <div className="auth-sub">{t('auth.techSub')}</div>
          <div className="auth-toggle">
            <button className={`auth-toggle-btn ${authForm.mode === 'login' ? 'active' : ''}`} onClick={() => setAuthForm(p => ({ ...p, mode: 'login' }))}>{t('auth.signIn')}</button>
            <button className={`auth-toggle-btn ${authForm.mode === 'register' ? 'active' : ''}`} onClick={() => setAuthForm(p => ({ ...p, mode: 'register' }))}>{t('auth.createAccount')}</button>
          </div>
          {authError && <div className="alert alert-red" style={{ marginBottom: 14 }}>{authError}</div>}
          {authForm.mode === 'register' && (
            <div className="form-row">
              <div className="form-group"><label>{t('req.fullName')}</label><input type="text" name="name" value={authForm.name} onChange={handleAuthInputChange} placeholder="John Smith" /></div>
              <div className="form-group"><label>{t('req.phone')}</label><input type="tel" name="phone" value={authForm.phone} onChange={handleAuthInputChange} placeholder="(801) 555-0100" /></div>
            </div>
          )}
          <div className="form-group"><label>{t('req.email')}</label><input type="email" name="email" value={authForm.email} onChange={handleAuthInputChange} placeholder="tech@email.com" /></div>
          <div className="form-group"><label>{t('auth.password')}</label><input type="password" name="password" value={authForm.password} onChange={handleAuthInputChange} placeholder="••••••••" /></div>
          {/* Technicians opt in to job texts the same way homeowners do — the
              relay texts both sides, so both sides need recorded consent.
              Unchecked by default; carriers reject pre-checked consent. */}
          {authForm.mode === 'register' && (
            <label className="same-check" style={{ alignItems: 'flex-start', lineHeight: 1.55, marginBottom: 12 }}>
              <input type="checkbox" name="smsConsent" checked={!!authForm.smsConsent} onChange={handleAuthInputChange} style={{ marginTop: 3 }} />
              <span style={{ fontSize: 12.5 }}>
                Text me about my jobs. I agree to receive text messages from {BRAND.name.full} about jobs
                assigned to me — scheduling, customer messages, and job updates. Message frequency varies.
                Message and data rates may apply. Reply STOP to opt out, HELP for help. Consent is not a
                condition of working with us. See our{' '}
                <span className="form-link" style={{ display: 'inline' }} onClick={(e) => { e.preventDefault(); goTo('screen-privacy'); }}>Privacy Policy</span>
                {' '}and{' '}
                <span className="form-link" style={{ display: 'inline' }} onClick={(e) => { e.preventDefault(); goTo('screen-terms'); }}>Terms of Service</span>.
              </span>
            </label>
          )}
          <button className="btn btn-primary btn-lg" onClick={() => handleAuthSubmit('vendor')}>
            {authForm.mode === 'login' ? t('auth.signIn') : t('auth.createAccount')}
          </button>
          {authForm.mode === 'login' && (
            <span className="form-link" onClick={() => openForgotPassword('screen-vendor-login')}>{t('auth.forgot')}</span>
          )}
        </div>
      </div>
    </div>
  );
}

// Forgot password — enter the account email, get a reset link.
export function ForgotPassword() {
  const { currentScreen, goTo, forgotEmail, setForgotEmail, forgotStatus, forgotFrom, authError, handleForgotPassword } = useApp();
  const { t } = useI18n();
  return (
    <div className={`screen ${currentScreen === 'screen-forgot-password' ? 'active' : ''}`}>
      <AuthNav onBack={() => goTo(forgotFrom)} />
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px 20px' }}>
        <div className="auth-card">
          <div className="auth-title">{t('forgot.title')}</div>
          <div className="auth-sub">{t('forgot.sub')}</div>
          {authError && <div className="alert alert-red" style={{ marginBottom: 14 }}>{authError}</div>}
          {forgotStatus ? (
            <div className="alert alert-green">{forgotStatus}</div>
          ) : (
            <>
              <div className="form-group"><label>{t('req.email')}</label><input type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="you@email.com" /></div>
              <button className="btn btn-primary btn-lg" onClick={handleForgotPassword}>{t('forgot.send')}</button>
            </>
          )}
          <span className="form-link" onClick={() => goTo(forgotFrom)}>{t('forgot.backToSignIn')}</span>
        </div>
      </div>
    </div>
  );
}

// Choose a new password — opened by the emailed ?reset=<token> link.
export function ResetPassword() {
  const { currentScreen, goTo, resetForm, setResetForm, isResetting, authError, handleResetPassword } = useApp();
  const { t } = useI18n();
  return (
    <div className={`screen ${currentScreen === 'screen-reset-password' ? 'active' : ''}`}>
      <AuthNav onBack={() => goTo('screen-home')} />
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px 20px' }}>
        <div className="auth-card">
          <div className="auth-title">{t('reset.title')}</div>
          <div className="auth-sub">{t('reset.sub')}</div>
          {authError && <div className="alert alert-red" style={{ marginBottom: 14 }}>{authError}</div>}
          <div className="form-group"><label>{t('reset.newPassword')}</label><input type="password" value={resetForm.password} onChange={(e) => setResetForm(p => ({ ...p, password: e.target.value }))} placeholder="••••••••" /></div>
          <div className="form-group"><label>{t('reset.confirm')}</label><input type="password" value={resetForm.confirm} onChange={(e) => setResetForm(p => ({ ...p, confirm: e.target.value }))} placeholder="••••••••" /></div>
          <button className="btn btn-primary btn-lg" disabled={isResetting} onClick={handleResetPassword}>
            {isResetting ? t('reset.saving') : t('reset.save')}
          </button>
          <span className="form-link" onClick={() => goTo('screen-login-choice')}>{t('forgot.backToSignIn')}</span>
        </div>
      </div>
    </div>
  );
}

// Admin (business operations) sign-in — stays English (internal tool).
export function AdminLogin() {
  const { currentScreen, goTo, authForm, authError, handleAuthInputChange, handleAuthSubmit, openForgotPassword } = useApp();
  return (
    <div className={`screen ${currentScreen === 'screen-admin-login' ? 'active' : ''}`}>
      <nav className="nav-public">
        <BrandMark />
        <button className="btn btn-ghost btn-sm" onClick={() => goTo('screen-home')}>← Back</button>
      </nav>
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px 20px' }}>
        <div className="auth-card">
          <div className="auth-title">Admin Portal</div>
          <div className="auth-sub">Business operations dashboard</div>
          {authError && <div className="alert alert-red" style={{ marginBottom: 14 }}>{authError}</div>}
          <div className="form-group"><label>Email</label><input type="email" name="email" value={authForm.email} onChange={handleAuthInputChange} placeholder={BRAND.adminEmailPlaceholder} /></div>
          <div className="form-group"><label>Password</label><input type="password" name="password" value={authForm.password} onChange={handleAuthInputChange} placeholder="••••••••" /></div>
          {/* Always 'login'. This screen has no way to create an account, so it
              must not inherit a Create Account mode left behind by the customer
              or technician portal — that turned Sign In into a registration and
              answered "Email already in use". */}
          <button className="btn btn-dark btn-lg" onClick={() => handleAuthSubmit('admin', 'login')}>Sign In</button>
          {/* The admin had no way back in from a forgotten password — the only
              route was the reset link, which you cannot reach without knowing
              to type ?reset= yourself. Both other portals have had this. */}
          <span className="form-link" onClick={() => openForgotPassword('screen-admin-login')}>Forgot password?</span>
        </div>
      </div>
    </div>
  );
}
